import { BLOG_STATUSES, BLOG_CATEGORIES } from "@ckgo2/shared/types";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import type { BlogPost } from "@ckgo2/shared/types";
import { useFacetedParams } from "@/hooks/useFacetedParams";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel from "@/components/common/FacetPanel";
import ListSortSelect from "@/components/common/ListSortSelect";
import {
  countBuckets,
  matchesFacet,
  pageSlice,
  compareCzech,
  compareNewest,
  labelLookup,
} from "@/lib/clientFacets";
import { Plus, Newspaper, Edit, Trash2, MapPin, Clock, Star, Copy } from "lucide-react";

interface BlogPostWithDest extends BlogPost {
  destinationName?: string;
}

const STATUS_COLORS: Record<string, string> = {
  published: "badge-primary",
  draft: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  archived: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
};

const PAGE_SIZE = 50;
const DEFAULT_SORT = "recent";

/**
 * Seznam článků blogu — filtry, hledání a řazení ve URL jako u ostatních seznamů adminu.
 * Dřív tu byl jen fulltext a kategorie: článek k destinaci se nedal vyfiltrovat, i když
 * ho editor k destinaci přiřazuje.
 */
export default function BlogPostsPage() {
  const { t } = useTranslation();
  const { getValues, search, page, params } = useFacetedParams();
  const queryClient = useQueryClient();

  const categoryLabels: Record<string, string> = {
    "travel-tips": t("content.blog.categories.travelTips"),
    culture: t("content.blog.categories.culture"),
    food: t("content.blog.categories.food"),
    nature: t("content.blog.categories.nature"),
    guides: t("content.blog.categories.guides"),
    news: t("content.blog.categories.news"),
  };

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
    archived: t("common.status.archived"),
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["blogPosts"],
    queryFn: () => api.get<ApiResponse<BlogPostWithDest[]>>("/api/admin/blog"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/blog/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blogPosts"] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/blog/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blogPosts"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(t("content.blog.list.confirmDelete", { title }))) {
      deleteMutation.mutate(id);
    }
  };

  const posts = useMemo(() => data?.data ?? [], [data]);

  // Popisky facety destinace skládá z řádků (seznam blogu nese `destinationName`), takže
  // stránka seznam destinací vůbec nestahuje.
  const destName = useMemo(
    () =>
      labelLookup(
        posts,
        (p) => p.destinationId,
        (p) => p.destinationName,
      ),
    [posts],
  );
  const sort = params.get("sort") ?? DEFAULT_SORT;
  const categorySel = getValues("category");
  const statusSel = getValues("status");
  const destSel = getValues("destinationId");

  const facets = useMemo(
    () => ({
      category: countBuckets(posts, (p) => p.category),
      status: countBuckets(posts, (p) => p.status),
      destinationId: countBuckets(posts, (p) => p.destinationId),
    }),
    [posts],
  );

  const filteredPosts = useMemo(() => {
    const rows = posts.filter(
      (p) =>
        matchesFacet(categorySel, p.category) &&
        matchesFacet(statusSel, p.status) &&
        matchesFacet(destSel, p.destinationId),
    );
    return sortBySearchMatch(rows, search, {
      name: (p) => p.title,
      other: (p) => [p.slug, p.destinationName, p.authorName],
      tieBreak: (a, b) =>
        sort === "title"
          ? compareCzech(a.title, b.title)
          : compareNewest(a.publishedAt ?? a.updatedAt, b.publishedAt ?? b.updatedAt),
    });
  }, [posts, search, categorySel, statusSel, destSel, sort]);

  const visible = pageSlice(filteredPosts, page, PAGE_SIZE);

  if (error) {
    return (
      <div className="card text-error-600 text-center">{t("content.blog.list.loadError")}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.blog.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("content.blog.list.subtitle")}</p>
        </div>
        <Link to="/blog/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.blog.list.newPost")}
        </Link>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("content.blog.list.searchPlaceholder")}
        total={filteredPosts.length}
        limit={PAGE_SIZE}
        isLoading={isLoading}
        isFetching={isFetching}
        actions={
          <ListSortSelect
            defaultValue={DEFAULT_SORT}
            options={[
              { value: "recent", label: t("shared.listSort.recent") },
              { value: "title", label: t("shared.listSort.titleAsc") },
            ]}
          />
        }
        filters={
          <>
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              order={{ enum: BLOG_STATUSES }}
              buckets={facets.status}
              labelFor={(v) => statusLabels[v] ?? v}
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="destinationId"
              order="name"
              buckets={facets.destinationId}
              labelFor={destName}
              nullLabel={t("shared.listSort.withoutDestination")}
            />
            <FacetPanel
              title={t("content.blog.list.category")}
              paramKey="category"
              order={{ enum: BLOG_CATEGORIES }}
              buckets={facets.category}
              labelFor={(v) => categoryLabels[v] ?? v}
            />
          </>
        }
      >
        {visible.length === 0 ? (
          <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
            <Newspaper className="mb-2 h-12 w-12 text-gray-300" />
            <p>{t("content.blog.list.empty")}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((post) => (
              <div key={post.id} className="card overflow-hidden p-0">
                {/* Image */}
                <div className="aspect-[3/2] bg-gray-100 dark:bg-gray-800">
                  {post.featuredImage ? (
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Newspaper className="h-16 w-16 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-gray-900 dark:text-white">
                        {post.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">/{post.slug}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[post.status] || ""}`}
                    >
                      {statusLabels[post.status] || post.status}
                    </span>
                  </div>

                  {/* Meta */}
                  <div className="mt-3 flex flex-wrap gap-2 text-sm">
                    <span className="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded px-2 py-0.5 text-xs font-medium">
                      {categoryLabels[post.category] || post.category}
                    </span>
                    {post.readTime && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {t("content.blog.list.readTime", { minutes: post.readTime })}
                      </span>
                    )}
                    {post.isFeatured && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Star className="h-3 w-3" />
                        {t("content.blog.list.featured")}
                      </span>
                    )}
                  </div>

                  {post.destinationName && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="h-3.5 w-3.5" />
                      {post.destinationName}
                    </div>
                  )}

                  {post.authorName && (
                    <p className="mt-1 text-xs text-gray-400">{post.authorName}</p>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                    <Link to={`/blog/${post.id}`} className="btn-ghost btn-sm">
                      <Edit className="h-4 w-4" />
                      {t("common.actions.edit")}
                    </Link>
                    <button
                      onClick={() => duplicateMutation.mutate(post.id)}
                      disabled={duplicateMutation.isPending}
                      className="btn-ghost btn-sm"
                      title={t("common.actions.duplicate")}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      title={t("common.actions.delete")}
                      onClick={() => handleDelete(post.id, post.title)}
                      disabled={deleteMutation.isPending}
                      className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </FacetedListLayout>
    </div>
  );
}
